/** Vorba cu serviciul asistentului.
 *
 *  DE CE NU ÎN `api.ts`. Acolo e regula «un singur loc care știe cum se
 *  vorbește cu serverul», și e o regulă bună: are ApiError, toast-uri, un tip
 *  per rută. Aici se vorbește cu ALT serviciu (`asistent`, nu control plane) și
 *  pe alt fel de legătură — un flux SSE care ține minute, pe care nu-l poate
 *  descrie `request<T>`. Turnat acolo, ar fi fost o excepție în mijlocul unui
 *  fișier construit ca să n-aibă excepții. Aici e tot ce știe modulul despre
 *  rețea, și modulul întreg pleacă dacă vreodată se mută.
 *
 *  UN TUR ARE DOUĂ JUMĂTĂȚI: fluxul pe care vine răspunsul, și — când modelul
 *  a cerut unelte — cererea următoare, cu rezultatele lor. Bucla e în
 *  `Asistent.tsx`, fiindcă executarea uneltelor e a editorului; aici e doar
 *  transportul.
 */

export type Bloc = { type: string; [cheie: string]: unknown };

export type MesajFisa = {
  rand: number;
  rol: "user" | "assistant";
  continut: Bloc[];
  creat_la: string;
};

export type Socoteala = {
  model: string;
  cereri: number;
  cost_usd: number;
  tokeni: { in: number; out: number; cache_scris: number; cache_citit: number };
};

export type Fisa = {
  id: string;
  project_id: string;
  unde: string;
  subiect: string | null;
  titlu: string | null;
  limba: string;
  mesaje: MesajFisa[];
  socoteala: Socoteala;
};

export type Apel = { id: string; nume: string; argumente: Record<string, unknown> };

export type Eveniment =
  | { fel: "gand"; text: string }
  | { fel: "text"; text: string }
  | { fel: "unealta"; apel: { id: string; name: string; input: unknown } }
  | { fel: "gata"; mesaj_id: number; stop: string; apeluri: Apel[];
      tokeni: Record<string, number>; refuz: unknown }
  | { fel: "eroare"; cod: string; detaliu?: string; pasi?: number };

export class EroareAsistent extends Error {
  constructor(readonly stare: number, readonly cod: string) {
    super(cod);
  }
}

const RADACINA = "/api/asistent";

async function json<T>(metoda: string, cale: string, corp?: unknown): Promise<T> {
  const raspuns = await fetch(RADACINA + cale, {
    method: metoda,
    credentials: "same-origin",
    headers: corp === undefined ? undefined : { "Content-Type": "application/json" },
    body: corp === undefined ? undefined : JSON.stringify(corp),
  });
  if (!raspuns.ok) {
    const payload = await raspuns.json().catch(() => null);
    throw new EroareAsistent(raspuns.status, payload?.error ?? raspuns.statusText);
  }
  return raspuns.status === 204 ? (null as T) : await raspuns.json();
}

export const deschide = (corp: {
  project_id: string; unde: string; subiect: string; limba: string;
  unelte: unknown[];
}) => json<Fisa>("POST", "/conversatii", corp);

export const citeste = (id: string) => json<Fisa>("GET", `/conversatii/${id}`);

export const listeaza = (projectId: string, subiect: string) =>
  json<{ conversatii: { id: string; subiect: string; titlu: string | null;
                        atinsa_la: string; mesaje: number }[] }>(
    "GET",
    `/conversatii?project_id=${encodeURIComponent(projectId)}`
    + `&subiect=${encodeURIComponent(subiect)}`);

export const sterge = (id: string) =>
  json<null>("DELETE", `/conversatii/${id}`);

export const sanatate = () =>
  json<{ ok: boolean; cheie: boolean; model: string }>("GET", "/health");

/** Un tur, eveniment cu eveniment, pe măsură ce vine.
 *
 *  `semnal` oprește fluxul când omul închide panoul sau apasă «Oprește»: cererea
 *  se rupe, iar serverul o vede închisă la primul `yield` care nu mai are unde
 *  să plece. Mesajul modelului tot se scrie în bază — turul rulează până la
 *  capăt pe server —, deci conversația rămâne întreagă.
 */
export async function* tur(cale: string, corp: unknown,
                           semnal?: AbortSignal): AsyncGenerator<Eveniment> {
  const raspuns = await fetch(RADACINA + cale, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(corp),
    signal: semnal,
  });
  if (!raspuns.ok || !raspuns.body) {
    const payload = await raspuns.json().catch(() => null);
    throw new EroareAsistent(raspuns.status, payload?.error ?? raspuns.statusText);
  }
  const cititor = raspuns.body.getReader();
  const decodor = new TextDecoder();
  let rest = "";
  while (true) {
    const { value, done } = await cititor.read();
    if (done) break;
    rest += decodor.decode(value, { stream: true });
    // CADRELE SE DESPART LA LINIE GOALĂ. Un `data:` poate veni tăiat în două
    // pachete TCP, deci nu se poate parsa nimic până nu s-a văzut despărțitorul.
    let taiere = rest.indexOf("\n\n");
    while (taiere >= 0) {
      const cadru = rest.slice(0, taiere);
      rest = rest.slice(taiere + 2);
      const ev = cadrul(cadru);
      if (ev) yield ev;
      taiere = rest.indexOf("\n\n");
    }
  }
}

function cadrul(cadru: string): Eveniment | null {
  let nume = "";
  const date: string[] = [];
  for (const linie of cadru.split("\n")) {
    if (linie.startsWith("event:")) nume = linie.slice(6).trim();
    else if (linie.startsWith("data:")) date.push(linie.slice(5).trim());
  }
  if (!nume) return null;
  let corp: any = null;
  try { corp = JSON.parse(date.join("\n")); } catch { corp = null; }
  switch (nume) {
    case "gand": return { fel: "gand", text: String(corp ?? "") };
    case "text": return { fel: "text", text: String(corp ?? "") };
    case "unealta": return { fel: "unealta", apel: corp };
    case "gata": return { fel: "gata", ...corp };
    case "eroare": return { fel: "eroare", ...corp };
    default: return null;
  }
}
